import { validatorFunc } from "../FormBuilder.types";

/**
 * Creates a validator function based on a regex pattern.
 *
 * @param pattern The RegExp pattern to test the value against.
 * @param error Optional custom error message for the validator.
 * @returns A validator function that checks if the value matches the regex pattern.
 */
export const regex = (pattern: RegExp, error: string): validatorFunc => {
  return (value: string, field: string) => {
    if (!!value && !pattern.test(value)) {
      return {
        valid: false,
        error: error || `Invalid ${field}.`,
      };
    }
    return { valid: true };
  };
};

/**
 * Creates a validator function to check if a field is required (non-empty).
 *
 * @param error Optional custom error message.
 * @returns A validator function that checks if the value is non-empty.
 */
export const required = (error?: string): validatorFunc => {
  return (value: string, field: string) => {
    if (!value) {
      return {
        valid: false,
        error: error || `${field} is required.`,
      };
    }
    return { valid: true };
  };
};

/**
 * Creates a validator function for email validation using a standard email regex pattern.
 *
 * @param error Optional custom error message.
 * @returns A validator function that checks if the value is a valid email.
 */
export const email = (error?: string): validatorFunc => {
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex(emailRegex, error || "Invalid email");
};

/**
 * Creates a validator function for password validation using a standard password regex pattern.
 *
 * @param error Optional custom error message.
 * @returns A validator function that checks if the value is a valid password.
 */
export const password = (error?: string): validatorFunc => {
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\/\-])[A-Za-z\d!@#$%^&*()_+{}\[\]:;<>,.?~\\/\-]{8,}$/;
  return regex(
    passwordRegex,
    error || "At least 8 characters, 1 upper case, 1 lower case, 1 special character & 1 digit required"
  );
};

/**
 * Creates a validator function that checks if a field contains only numeric characters.
 *
 * @param error Optional custom error message.
 * @returns A validator function that checks if the value contains only numeric characters.
 */
export const numericOnly = (error?: string): validatorFunc => {
  return regex(/^[0-9]*$/, error || "Must contain numeric characters only.");
};

/**
 * Creates a validator function that checks if a field contains only alphabetic characters.
 *
 * @param error Optional custom error message.
 * @returns A validator function that checks if the value contains only alphabetic characters.
 */
export const alphaOnly = (error?: string): validatorFunc => {
  return regex(/^[a-zA-Z]*$/, error || "Must contain alphabetic characters only.");
};

/**
 * Creates a validator function that checks if a field contains minimum number of characters.
 * @param limit minimum number of characters
 * @param error Optional custom error message.
 * @returns A validator function that checks if the value contains minimum number of characters.
 */
export const minChar = (limit: number, error?: string): validatorFunc => {
  return (value: string, field: string) => {
    if (!value || (!!value && value.length >= limit)) {
      return { valid: true };
    }
    return {
      valid: false,
      error: error || `${field} must be at least ${limit} long.`,
    };
  };
};

/**
 * Creates a validator function that checks if a field contains maximum number of characters.
 * @param limit maximum number of characters
 * @param error Optional custom error message.
 * @returns A validator function that checks if the value contains maximum number of characters.
 */
export const maxChar = (limit: number, error?: string): validatorFunc => {
  return (value: string, field: string) => {
    if (!value || (!!value && value.length <= limit)) {
      return { valid: true };
    }
    return {
      valid: false,
      error: error || `${field} can't be longer then ${limit}.`,
    };
  };
};

/**
 * Creates a validator function that checks if a field contains exact number of characters.
 * @param limit exact number of characters
 * @param error Optional custom error message.
 * @returns A validator function that checks if the value contains exact number of characters.
 */
export const charLength = (limit: number, error?: string): validatorFunc => {
  return (value: string, field: string) => {
    if (!!value && value.length !== limit) {
      return {
        valid: false,
        error: error || `${field} must be ${limit} characters long.`,
      };
    }
    return { valid: true };
  };
};

/**
 * Creates a custom validator function that can be configured to check field value against custom rules or current form state.
 * @param validator custom validator function
 * @returns A validator function that checks if the value contains exact number of characters.
 */
export const custom = (validator: validatorFunc): validatorFunc => {
  return validator;
};

/**
 * Executes an array of validator functions against a given value.
 *
 * @param value The value to be validated.
 * @param field The name of the field (used in error messages).
 * @param validators An array of validator functions to apply.
 * @returns An object with a 'valid' boolean and an optional 'error' message.
 */
export const executeValidators = (value: string, field: string, validators: validatorFunc[]) => {
  for (const validate of validators) {
    const result = validate(value, field);
    if (!result.valid) {
      return result;
    }
  }
  return { valid: true };
};
